import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    ListItem,
    ListItemText,
    Typography,
} from '@material-ui/core';

import { ActionButton } from '@klp/design/ActionButton';
import { Block } from '@klp/design/Block';
import { Illustration, IconName } from '@klp/design/Illustrations';

import {
    useGrantSuperUserMutation,
    useRevokeSuperUserMutation,
} from '../rtkApi';

interface IToggleSuperUserProps {
    isSuperUser: boolean;
    email: string;
    onSuccess: () => void;
}

export const ToggleSuperUser: React.FC<IToggleSuperUserProps> = ({
    isSuperUser,
    email,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [displayDialog, setDisplayDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [grantSuperUser] = useGrantSuperUserMutation();
    const [revokeSuperUser] = useRevokeSuperUserMutation();

    const handleToggleSuperUser = () => {
        setDisplayDialog(true);
    };

    const handleToggleSuperUserClose = () => {
        if (!isLoading) {
            setDisplayDialog(false);
        }
    };

    const handleToggleSuperUserConfirmed = async () => {
        setIsLoading(true);
        try {
            if (isSuperUser) {
                await revokeSuperUser(email).unwrap();
            } else {
                await grantSuperUser(email).unwrap();
            }
            onSuccess();
            setDisplayDialog(false);
        } catch (error) {
            // Error handling is done by RTK Query
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Block>
                <ListItem
                    dense={true}
                    button={true}
                    onClick={handleToggleSuperUser}
                >
                    <Block paddingRight="compact">
                        <Illustration icon={IconName.PROFILE} size="medium" />
                    </Block>
                    <ListItemText>
                        {isSuperUser ? (
                            <Typography>
                                {t('userSearch.revokeSuperUser')}
                            </Typography>
                        ) : (
                            <Typography>
                                {t('userSearch.grantSuperUser')}
                            </Typography>
                        )}
                    </ListItemText>
                </ListItem>
            </Block>
            <Dialog open={displayDialog} onClose={handleToggleSuperUserClose}>
                <DialogContent>
                    {isSuperUser ? (
                        <DialogContentText>
                            {t('userSearch.revokeSuperUserQuestion', { email })}
                        </DialogContentText>
                    ) : (
                        <>
                            <DialogContentText>
                                {t('userSearch.grantSuperUserQuestion', {
                                    email,
                                })}
                            </DialogContentText>
                            <Typography variant="caption" color="textSecondary">
                                {t('listUsers.superUserExplanation')}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <ActionButton
                        actionType="cancel"
                        onClick={handleToggleSuperUserClose}
                        translation="general.decline"
                        disabled={isLoading}
                    />
                    <ActionButton
                        actionType="forward"
                        onClick={handleToggleSuperUserConfirmed}
                        translation="general.confirm"
                        isLoading={isLoading}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
